import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Typography, Alert, Steps, Row, Col, Space, Spin } from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  MailOutlined,
  CheckCircleOutlined,
  VideoCameraOutlined,
  TeamOutlined,
  RocketOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../utils/firebase';
import { useStripe } from '../hooks/useStripe';

const { Title, Text } = Typography;
const { Step } = Steps;

interface UserDetails {
  name: string;
  email: string;
  password: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { createCheckoutSession } = useStripe();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [userDetails, setUserDetails] = useState<UserDetails>({
    name: '',
    email: '',
    password: ''
  });
  const [selectedPlan, setSelectedPlan] = useState<string>('standard');
  const [partnerRef, setPartnerRef] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    const slug = ref || localStorage.getItem('spectreviews_ref');
    if (ref) {
      setPartnerRef(ref);
      localStorage.setItem('spectreviews_ref', ref);
    } else if (slug) {
      setPartnerRef(slug);
    }
    if (slug) {
      const cookieKey = `spectreviews_viewed_${slug}`;
      const isUnique = !document.cookie.includes(cookieKey);
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/track-partner-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, unique: isUnique }),
      }).catch(() => {});
      if (isUnique) {
        document.cookie = `${cookieKey}=1; max-age=2592000; path=/; SameSite=Lax`;
      }
    }
  }, []);

  const handleFirstStep = (values: UserDetails) => {
    setUserDetails(values);
    setCurrentStep(1);
    setError(null);
  };

  const handleBack = () => {
    setCurrentStep(0);
    setError(null);
  };

  const onFinish = async () => {
    setError(null);
    setIsLoading(true);
    
    try {
      // Create user with Firebase directly
      const userCredential = await createUserWithEmailAndPassword(auth, userDetails.email, userDetails.password);
      const user = userCredential.user;
      
      // Create user document in Firestore with free plan initially
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: userDetails.name,
        planType: 'free', // Always start as free, Stripe will update this
        planStatus: 'active',
        usage: {
          videosThisMonth: 0,
          scriptsThisMonth: 0,
          videosAllTime: 0,
          scriptsAllTime: 0,
          lastVideoCreated: null,
          lastScriptCreated: null
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        ...(partnerRef ? { referredBy: partnerRef } : {}),
      });
      
      localStorage.removeItem('spectreviews_ref');

      console.log('creating GHL contact...');

      try {
        const token = await user.getIdToken();
        // Note: Use buildApiUrl or authenticatedFetch from authProvider if impersonation support needed, but since new user, direct fetch is fine
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/create-ghl-contact`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uid: user.uid,
            email: user.email,
            name: userDetails.name,
            planType: selectedPlan  // Use selectedPlan here (not always 'free')
          }),
        });
        
        if (!response.ok) {
          // CRM sync is non-critical background housekeeping — never block or
          // alarm the user mid-signup. Log and continue to checkout.
          console.error('Failed to create GHL contact:', await response.text());
        }
      } catch (ghlError) {
        console.error('Failed to create GHL contact:', ghlError);
      }

      // A plan is always selected — redirect to Stripe checkout (card required,
      // first month base free). The backend maps the plan to its Stripe prices.
      localStorage.setItem('pendingPlan', selectedPlan);
      createCheckoutSession(selectedPlan);
      // createCheckoutSession handles the redirect.
      return;

    } catch (error: any) {
      console.log('Registration error:', error);
      
      // Handle Firebase error codes
      if (error.code === 'auth/email-already-in-use') {
        try {
          const cred = await signInWithEmailAndPassword(auth, userDetails.email, userDetails.password);
          // Ensure a Firestore user doc exists (the backend's auth check 404s
          // without one, which surfaces as "failed to start checkout"). Merge so
          // we never clobber an existing profile/plan.
          await setDoc(doc(db, 'users', cred.user.uid), {
            uid: cred.user.uid,
            email: cred.user.email,
            displayName: userDetails.name,
            updatedAt: new Date(),
          }, { merge: true });
          localStorage.setItem('pendingPlan', selectedPlan);
          createCheckoutSession(selectedPlan);
          return;
        } catch (signInErr: any) {
          if (signInErr.code === 'auth/wrong-password' || signInErr.code === 'auth/invalid-credential') {
            setError('This email is already registered with a different password. Please sign in or reset your password.');
            return;
          }
        }
        setError('This email is already registered. Please sign in or use a different email.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (error.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection and try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(error.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const plans = [
    {
      id: 'standard',
      name: 'Agent',
      price: '$25',
      period: '/month',
      features: [
        '3 reports included / month',
        'Then $10 per additional report',
        'Custom branding',
        'Client collaboration'
      ],
      icon: <VideoCameraOutlined style={{ fontSize: 32 }} />,
      recommended: true
    },
    {
      id: 'pro',
      name: 'Team',
      price: '$149',
      period: '/month',
      features: [
        '20 reports included / month',
        'Then $6 per additional report',
        'Custom branding',
        'Priority support'
      ],
      icon: <RocketOutlined style={{ fontSize: 32 }} />,
      recommended: false
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <Card 
        style={{
          width: '100%',
          maxWidth: currentStep === 0 ? '400px' : '800px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          transition: 'max-width 0.3s ease'
        }}
        bodyStyle={{ padding: '40px' }}
      >
        <Steps current={currentStep} style={{ marginBottom: '32px' }}>
          <Step title={<span style={{ fontSize: '14px' }}>Account</span>} />
          <Step title={<span style={{ fontSize: '14px' }}>Plan Selection</span>} />
        </Steps>

        {currentStep === 0 ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <Title level={2} style={{ marginBottom: '8px' }}>
                Join Spectreviews
              </Title>
              <p style={{ color: '#666', margin: 0 }}>
                Create your account to get started
              </p>
            </div>

            <Form 
              name="register_step1" 
              onFinish={handleFirstStep} 
              layout="vertical"
              size="large"
              requiredMark={false}
              initialValues={userDetails}
            >
              <Form.Item
                name="name"
                rules={[{ required: true, message: 'Please input your name!' }]}
              >
                <Input 
                  prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} 
                  placeholder="Full Name" 
                />
              </Form.Item>

              <Form.Item
                name="email"
                rules={[{ required: true, type: 'email', message: 'Please input a valid email!' }]}
              >
                <Input 
                  prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} 
                  placeholder="Email" 
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, min: 6, message: 'Password must be at least 6 characters!' }]}
              >
                <Input.Password 
                  prefix={<LockOutlined style={{ color: '#bfbfbf' }} />} 
                  placeholder="Password" 
                />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" block style={{ height: '40px' }}>
                  Continue
                </Button>
              </Form.Item>
            </Form>
          </>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <Title level={2} style={{ marginBottom: '8px' }}>
                Choose Your Plan
              </Title>
              <p style={{ color: '#666', margin: 0 }}>
                Your first month's base is free — you're only billed for reports beyond your plan's included amount.
              </p>
            </div>

            <Row gutter={16} style={{ marginBottom: '32px' }}>
              {plans.map((plan) => (
                <Col xs={24} md={8} key={plan.id}>
                  <Card
                    hoverable
                    onClick={() => setSelectedPlan(plan.id)}
                    style={{
                      height: '100%',
                      cursor: 'pointer',
                      border: selectedPlan === plan.id ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      position: 'relative',
                      marginBottom: '16px'
                    }}
                    bodyStyle={{ 
                      padding: '24px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {plan.recommended && (
                      <div style={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#1890ff',
                        color: 'white',
                        padding: '4px 16px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        RECOMMENDED
                      </div>
                    )}

                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                      <div style={{ color: '#1890ff', marginBottom: '12px' }}>
                        {plan.icon}
                      </div>
                      <Title level={4} style={{ margin: 0 }}>
                        {plan.name}
                      </Title>
                    </div>

                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                      <span style={{ fontSize: '32px', fontWeight: 'bold' }}>
                        {plan.price}
                      </span>
                      <span style={{ color: '#666', fontSize: '16px' }}>
                        {plan.period}
                      </span>
                    </div>

                    <ul style={{ 
                      listStyle: 'none', 
                      padding: 0, 
                      margin: 0,
                      flex: 1 
                    }}>
                      {plan.features.map((feature, index) => (
                        <li key={index} style={{ 
                          marginBottom: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '14px'
                        }}>
                          <CheckCircleOutlined style={{ 
                            color: '#52c41a', 
                            marginRight: '8px',
                            fontSize: '16px'
                          }} />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {selectedPlan === plan.id && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <CheckCircleOutlined style={{ color: 'white', fontSize: '14px' }} />
                      </div>
                    )}
                  </Card>
                </Col>
              ))}

              {/* Brokerage — contact sales (not self-serve) */}
              <Col xs={24} md={8}>
                <Card
                  hoverable
                  style={{ height: '100%', border: '1px solid #d9d9d9', marginBottom: '16px' }}
                  bodyStyle={{ padding: '24px', height: '100%', display: 'flex', flexDirection: 'column' }}
                >
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ color: '#1890ff', marginBottom: '12px' }}>
                      <TeamOutlined style={{ fontSize: 32 }} />
                    </div>
                    <Title level={4} style={{ margin: 0 }}>Brokerage</Title>
                  </div>
                  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <span style={{ fontSize: '24px', fontWeight: 'bold' }}>Let's talk</span>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
                    {['Multiple users, one account', 'Volume pricing', 'Dedicated support'].map((feature, index) => (
                      <li key={index} style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px', fontSize: '16px' }} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button block href="mailto:info@spectreviews.com" style={{ marginTop: '16px' }}>
                    Contact Us
                  </Button>
                </Card>
              </Col>
            </Row>

            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: '16px' }}
              />
            )}

            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button onClick={handleBack} size="large">
                Back
              </Button>
              <Button 
                type="primary" 
                onClick={onFinish} 
                loading={isLoading} 
                size="large"
                icon={isLoading ? <LoadingOutlined /> : undefined}
              >
                {isLoading ? 'Creating Account...' : 'Continue to Payment'}
              </Button>
            </Space>
          </>
        )}
        
        <div style={{ 
          textAlign: 'center', 
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid #f0f0f0'
        }}>
          <span style={{ color: '#666' }}>Already have an account? </span>
          <Link to="/login" style={{ color: '#1890ff', fontWeight: 500 }}>
            Sign in
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default Register;